import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Chat.css";
import { fetchDocumentUrl } from "src/api/chat";
import { useNavigate, useParams } from "react-router";
import { useApi } from "src/api/api";
import { logout } from "src/helpers/logout";
import { useOrgContext } from "src/contexts/org-context";
import useDocumentTitle from "src/helpers/useDocumentTitle";
import { useConfigFromApi } from "src/helpers/load-org-from-api";
import DefaultWrapper from "src/components/DefaultWrapper";
import { useLocation } from "react-router-dom";
import { useSubOrgContext } from "src/contexts/suborg-items-context";
import { useViewportHeight } from "src/helpers/viewportHeight";
import { useAutoscroll } from "./use-autoscroll";
import { useOpenAIRealtime } from "./use-openai-realtime";
import { useMessageStream } from "./use-message-stream";
import { useComments } from "./use-comments";
import { CommentDialog } from "./dialogs/CommentDialog";
import { EditCommentsDialog } from "./dialogs/EditCommentsDialog";
import { DeleteConfirmDialog } from "./dialogs/DeleteConfirmDialog";
import { SearchSuborgDialog } from "./dialogs/SearchSuborgDialog";
import { EnlargedImageOverlay } from "./dialogs/EnlargedImageOverlay";
import { QuizNotFoundDialog } from "./dialogs/QuizNotFoundDialog";
import { AudioDialog } from "./components/AudioDialog";
import { MessageList } from "./components/MessageList";
import { ChatInput } from "./components/ChatInput";
import { resolveSourceUrl } from "./components/Citation";
import { useInitialGreeting } from "./use-initial-greeting";
import { useQueryType } from "./use-query-type";
import { useChatBootstrap } from "./use-chat-bootstrap";

const Chat = ({
  hideChatHeader = false,
  isFrontifyWidget = false,
}: {
  hideChatHeader?: boolean;
  isFrontifyWidget?: boolean;
}) => {
  const messageInputRef = React.useRef<HTMLDivElement>(null);
  const { org_slug = "", sub_org_slug = "", quiz_id = "" } = useParams();
  const api = useApi(org_slug);
  const {
    org_logo,
    first_name,
    setFirstName,
    setLastName,
    pageTitle,
    bot_greeting_msg,
    bot_typing_msg,
    primary_color,
    secondary_color,
    query_type,
    userHasCommentPermission,
    replyPlaceholder,
    override_page_title,
  } = useOrgContext();
  const { configLoaded } = useConfigFromApi(org_slug, true);
  useDocumentTitle(pageTitle || "TeamMate");
  useViewportHeight();
  const amplitudeRef1 = useRef<SVGPathElement>(null);
  const amplitudeRef2 = useRef<SVGPathElement>(null);
  const { setCurrentSuborgTitle, cards } = useSubOrgContext();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [sendInitMessage, setSendInitMessage] = useState(false);
  const [enlargeImage, setEnlargeImage] = useState<string | boolean>(false);
  const { queryType, queryTypeRef, suborgConfigLoaded } = useQueryType({
    subOrgSlug: sub_org_slug,
    quizId: quiz_id,
    queryTypeFromConfig: query_type,
  });
  const [isMessageIncoming, setIsMessageIncoming] = useState(false);
  const { messagesListRef, beginAutoscroll } = useAutoscroll({
    isMessageIncoming,
  });
  const { messages, setMessages, sendMessage, latestMessageRef } =
    useMessageStream({
      api,
      orgSlug: org_slug,
      subOrgSlug: sub_org_slug,
      quizId: quiz_id,
      messagesListRef,
      beginAutoscroll,
      navigate,
      isMessageIncoming,
      setIsMessageIncoming,
    });
  const {
    audioInputDialog,
    voiceLoading,
    muted,
    beginAudio,
    cancelAudio,
    toggleMute,
  } = useOpenAIRealtime({
    orgSlug: org_slug,
    subOrgSlug: sub_org_slug,
    amplitudeRef1,
    amplitudeRef2,
    messages,
    setMessages,
  });
  const [showSearchDropdown, setShowSearchDropdown] = useState<null | string>(
    null,
  );
  const [quizNotFound, setQuizNotFound] = useState(false);
  const {
    messageDropdown,
    commentDialog,
    setCommentDialog,
    editCommentDialog,
    setEditCommentDialog,
    handleDropdownToggle,
    handleDropdownClick,
    handleAddComment,
    handleSubmitFinalComment,
    handleDeleteComment,
    handleUpdateComment,
  } = useComments({
    api,
    orgSlug: org_slug,
    subOrgSlug: sub_org_slug,
    messages,
    resendQuery: useCallback(
      (originalMessage: string) =>
        sendMessage(originalMessage, queryType || query_type || ""),
      [sendMessage, queryType, query_type],
    ),
  });

  const handleMessage = useCallback(() => {
    const messageInput = messageInputRef.current;

    if (messageInput) {
      const text = messageInput.outerText;

      // Add the message to the chat
      sendMessage(text, queryTypeRef.current);

      // Clear the input
      messageInput.innerHTML = "";
    }
  }, [sendMessage, queryTypeRef]);

  const onCitationClick = useCallback(
    (url: string) => {
      if (isFrontifyWidget && window.parent !== window) {
        // Parent frame is already at *.frontify.com — let it navigate using
        // its own origin (Frontify-relative paths work there as-is).
        window.parent.postMessage({ type: "frontify-navigate", url }, "*");
      } else {
        // Standalone chat: turn the Frontify-relative path into an absolute
        // URL before opening, otherwise window.open routes to our domain.
        window.open(resolveSourceUrl(url), "_blank");
      }
    },
    [isFrontifyWidget],
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleMessage();
    }
  };

  useChatBootstrap({
    api,
    orgSlug: org_slug,
    subOrgSlug: sub_org_slug,
    firstName: first_name,
    setFirstName,
    setLastName,
    setCurrentSuborgTitle,
  });

  useInitialGreeting({
    configLoaded,
    suborgConfigLoaded,
    orgSlug: org_slug,
    subOrgSlug: sub_org_slug,
    quizId: quiz_id,
    queryType,
    queryTypeFromConfig: query_type,
    botGreetingMsg: bot_greeting_msg,
    messagesLength: messages.length,
    api,
    navigate,
    queryTypeRef,
    setMessages,
    onQuizNotFound: () => setQuizNotFound(true),
  });

  useEffect(() => {
    if (sendInitMessage) return;
    const messageInput = messageInputRef.current;
    if (state && state.message && state.message.length > 0 && messageInput) {
      messageInput.innerHTML = state.message;
      setSendInitMessage(true);
      handleMessage();
    }
  }, [state, handleMessage, sendInitMessage, setSendInitMessage]);

  const openDocument = async (doc: string) => {
    try {
      const result = await fetchDocumentUrl(
        api,
        sub_org_slug && sub_org_slug.length > 0 ? sub_org_slug : org_slug,
        doc,
      );

      if (result.url) {
        window.open(result.url, "_blank");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  return (
    <DefaultWrapper
      configLoaded={configLoaded && suborgConfigLoaded}
      primary_color={primary_color}
      secondary_color={secondary_color}
      org_slug={org_slug}
      org_logo={org_logo}
      logout={logout}
      page="chat"
      override_page_title={override_page_title}
      hideHeader={hideChatHeader}
    >
      <div className="w-full h-[var(--vh)] max-w-2xl flex flex-col ml-auto mr-auto relative overflow-hidden">
        <MessageList
          messages={messages}
          messagesListRef={messagesListRef}
          latestMessageRef={latestMessageRef}
          isMessageIncoming={isMessageIncoming}
          botTypingMsg={bot_typing_msg}
          userHasCommentPermission={userHasCommentPermission}
          queryType={queryType}
          messageDropdown={messageDropdown}
          onDropdownToggle={handleDropdownToggle}
          onDropdownClick={handleDropdownClick}
          onEnlargeImage={(url) => setEnlargeImage(url)}
          onOpenDocument={openDocument}
          onSearchInSuborg={(text) => setShowSearchDropdown(text)}
          onCitationClick={onCitationClick}
        />
        <SearchSuborgDialog
          query={showSearchDropdown}
          cards={cards}
          orgSlug={org_slug}
          navigate={navigate}
          setShowSearchDropdown={setShowSearchDropdown}
          setSendInitMessage={setSendInitMessage}
        />
        <EnlargedImageOverlay
          image={enlargeImage}
          onClose={() => setEnlargeImage(false)}
        />
        <AudioDialog
          audioInputDialog={audioInputDialog}
          voiceLoading={voiceLoading}
          muted={muted}
          amplitudeRef1={amplitudeRef1}
          amplitudeRef2={amplitudeRef2}
          cancelAudio={cancelAudio}
          toggleMute={toggleMute}
        />
        <ChatInput
          audioInputDialog={audioInputDialog}
          replyPlaceholder={replyPlaceholder}
          queryType={queryType}
          messageInputRef={messageInputRef}
          onKeyDown={handleKeyDown}
          onSend={handleMessage}
          onBeginAudio={beginAudio}
        />
        <CommentDialog
          commentDialog={commentDialog}
          setCommentDialog={setCommentDialog}
          handleAddComment={handleAddComment}
          handleSubmitFinalComment={handleSubmitFinalComment}
        />
        <EditCommentsDialog
          editCommentDialog={editCommentDialog}
          setEditCommentDialog={setEditCommentDialog}
          handleUpdateComment={handleUpdateComment}
        />
        <DeleteConfirmDialog
          editCommentDialog={editCommentDialog}
          setEditCommentDialog={setEditCommentDialog}
          handleDeleteComment={handleDeleteComment}
        />
        <QuizNotFoundDialog
          show={quizNotFound}
          onNavigateHome={() => navigate(`/${org_slug}/home`)}
        />
      </div>
    </DefaultWrapper>
  );
};

export default Chat;
